import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { toast, Toaster } from 'sonner';
import { Plus, X, Mail, Phone, Globe, Save, Power } from 'lucide-react';
import PurchaseOrderEmail from '@/components/PurchaseOrderEmail';
import WhatsAppPO from '@/components/WhatsAppPO';

const EMPTY = {
    name: '', category: 'Apparel',
    contact_name: '', contact_email: '', contact_phone: '',
    country: '', lead_time_weeks: 8, product_slugs: [], notes: '',
};

export default function AdminManufacturers() {
    const [items, setItems] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showNew, setShowNew] = useState(false);
    const [draft, setDraft] = useState(EMPTY);
    const [edits, setEdits] = useState({});

    const reload = async () => {
        const [m, p] = await Promise.all([api.get('/manufacturers'), api.get('/products')]);
        setItems(m.data);
        setProducts(p.data);
    };

    useEffect(() => {
        reload().finally(() => setLoading(false));
    }, []);

    const create = async (e) => {
        e.preventDefault();
        try {
            await api.post('/manufacturers', draft);
            toast.success(`${draft.name} created`);
            setShowNew(false);
            setDraft(EMPTY);
            reload();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Create failed');
        }
    };

    const setEdit = (id, key, val) => {
        setEdits((e) => ({ ...e, [id]: { ...(e[id] || {}), [key]: val } }));
    };

    const toggleProduct = (id, slug, current) => {
        const set = new Set(current || []);
        if (set.has(slug)) set.delete(slug); else set.add(slug);
        setEdit(id, 'product_slugs', [...set]);
    };

    const save = async (m) => {
        const draft = edits[m.id];
        if (!draft) return;
        try {
            await api.patch(`/manufacturers/${m.id}`, draft);
            toast.success(`Saved ${m.name}`);
            setEdits((e) => { const c = { ...e }; delete c[m.id]; return c; });
            reload();
        } catch {
            toast.error('Save failed');
        }
    };

    const toggleActive = async (m) => {
        try {
            await api.patch(`/manufacturers/${m.id}`, { active: !m.active });
            toast.success(`${m.name} ${!m.active ? 'enabled' : 'disabled'}`);
            reload();
        } catch {
            toast.error('Update failed');
        }
    };

    if (loading) return <div className="text-zinc-400 font-mono-data">Loading…</div>;

    return (
        <div data-testid="admin-manufacturers">
            <Toaster theme="dark" />

            <div className="flex items-center justify-between mb-6">
                <p className="font-body text-zinc-400 max-w-2xl">
                    Manage the suppliers behind each product line. Assign which products each manufacturer makes —
                    this drives the stock take and supplier emails.
                </p>
                <button data-testid="add-manufacturer-btn"
                        onClick={() => setShowNew(!showNew)}
                        className="bg-brand hover:bg-brand-hover text-black px-4 py-2 font-display uppercase tracking-wider text-sm inline-flex items-center gap-2">
                    {showNew ? <X size={14} /> : <Plus size={14} />}
                    {showNew ? 'Cancel' : 'Add Manufacturer'}
                </button>
            </div>

            {showNew && (
                <form onSubmit={create} data-testid="manufacturer-new-form"
                      className="border-hairline p-6 mb-8 grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="md:col-span-2 space-y-3">
                        <input required placeholder="Manufacturer name" data-testid="mfr-name"
                               value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                               className="w-full bg-surface border border-zinc-800 px-3 py-2 text-white" />
                        <div className="grid grid-cols-3 gap-3">
                            <select value={draft.category}
                                    onChange={(e) => setDraft({ ...draft, category: e.target.value })}
                                    className="bg-surface border border-zinc-800 px-3 py-2 text-white">
                                {['Apparel', 'Mats', 'Equipment', 'Other'].map((c) => <option key={c}>{c}</option>)}
                            </select>
                            <input placeholder="Country" value={draft.country}
                                   onChange={(e) => setDraft({ ...draft, country: e.target.value })}
                                   className="bg-surface border border-zinc-800 px-3 py-2 text-white" />
                            <input type="number" min="1" placeholder="Lead time (weeks)"
                                   value={draft.lead_time_weeks}
                                   onChange={(e) => setDraft({ ...draft, lead_time_weeks: parseInt(e.target.value || '8', 10) })}
                                   className="bg-surface border border-zinc-800 px-3 py-2 text-white" />
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                            <input placeholder="Contact name" value={draft.contact_name}
                                   onChange={(e) => setDraft({ ...draft, contact_name: e.target.value })}
                                   className="bg-surface border border-zinc-800 px-3 py-2 text-white" />
                            <input type="email" placeholder="Contact email" value={draft.contact_email}
                                   onChange={(e) => setDraft({ ...draft, contact_email: e.target.value })}
                                   className="bg-surface border border-zinc-800 px-3 py-2 text-white" />
                            <input placeholder="Phone" value={draft.contact_phone}
                                   onChange={(e) => setDraft({ ...draft, contact_phone: e.target.value })}
                                   className="bg-surface border border-zinc-800 px-3 py-2 text-white" />
                        </div>
                        <textarea rows={3} placeholder="Notes"
                                  value={draft.notes}
                                  onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                                  className="w-full bg-surface border border-zinc-800 px-3 py-2 text-white" />
                    </div>
                    <div>
                        <div className="font-mono-data text-[10px] uppercase text-zinc-500 mb-2">Supplies these products</div>
                        <div className="space-y-2 max-h-72 overflow-y-auto pr-2">
                            {products.map((p) => (
                                <label key={p.slug} className="flex items-center gap-2 font-mono-data text-xs cursor-pointer">
                                    <input type="checkbox" checked={draft.product_slugs.includes(p.slug)}
                                           onChange={() => {
                                               const s = new Set(draft.product_slugs);
                                               if (s.has(p.slug)) s.delete(p.slug); else s.add(p.slug);
                                               setDraft({ ...draft, product_slugs: [...s] });
                                           }} />
                                    {p.name}
                                </label>
                            ))}
                        </div>
                        <button data-testid="mfr-create-submit" type="submit"
                                className="mt-4 w-full bg-brand hover:bg-brand-hover text-black px-4 py-2 font-display uppercase tracking-wider text-sm">
                            Create Manufacturer
                        </button>
                    </div>
                </form>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-zinc-800 border-hairline">
                {items.map((m) => {
                    const draft = edits[m.id] || {};
                    const dirty = Object.keys(draft).length > 0;
                    const slugs = draft.product_slugs !== undefined ? draft.product_slugs : (m.product_slugs || []);
                    return (
                        <div key={m.id} data-testid={`mfr-card-${m.id}`}
                             className={`bg-ink p-6 ${!m.active ? 'opacity-50' : ''}`}>
                            <div className="flex items-start justify-between gap-3 mb-4">
                                <div>
                                    <div className="font-mono-data text-[10px] uppercase text-brand tracking-widest">
                                        {m.category}
                                    </div>
                                    <h3 className="font-display text-2xl uppercase tracking-tighter">{m.name}</h3>
                                    {m.country && (
                                        <div className="font-mono-data text-xs text-zinc-400 mt-1 flex items-center gap-1">
                                            <Globe size={12} /> {m.country} · {m.lead_time_weeks}wk lead
                                        </div>
                                    )}
                                </div>
                                <button onClick={() => toggleActive(m)}
                                        data-testid={`mfr-toggle-${m.id}`}
                                        className={`p-2 border ${m.active ? 'border-zinc-800 hover:border-danger hover:text-danger' : 'border-brand text-brand'}`}
                                        title={m.active ? 'Disable' : 'Enable'}>
                                    <Power size={14} />
                                </button>
                            </div>

                            <div className="space-y-1 mb-4 font-mono-data text-xs">
                                {m.contact_name && <div className="text-zinc-300">{m.contact_name}</div>}
                                {m.contact_email && (
                                    <a href={`mailto:${m.contact_email}`}
                                       className="flex items-center gap-2 text-zinc-400 hover:text-brand">
                                        <Mail size={12} /> {m.contact_email}
                                    </a>
                                )}
                                {m.contact_phone && (
                                    <a href={`tel:${m.contact_phone}`}
                                       className="flex items-center gap-2 text-zinc-400 hover:text-brand">
                                        <Phone size={12} /> {m.contact_phone}
                                    </a>
                                )}
                            </div>

                            {m.notes && (
                                <p className="font-body text-sm text-zinc-300 mb-4 border-l-2 border-brand/40 pl-3">
                                    {m.notes}
                                </p>
                            )}

                            <div className="font-mono-data text-[10px] uppercase text-zinc-500 mb-2">
                                Products supplied
                            </div>
                            <div className="flex flex-wrap gap-1.5 mb-3">
                                {products.map((p) => {
                                    const on = slugs.includes(p.slug);
                                    return (
                                        <button key={p.slug}
                                                onClick={() => toggleProduct(m.id, p.slug, slugs)}
                                                data-testid={`mfr-${m.id}-product-${p.slug}`}
                                                className={`px-2 py-1 font-mono-data text-[10px] uppercase border transition-colors ${
                                                    on ? 'bg-brand text-black border-brand' : 'border-zinc-800 text-zinc-500 hover:border-zinc-600'
                                                }`}>
                                                {p.name.split('—')[0].trim()}
                                        </button>
                                    );
                                })}
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                                {dirty && (
                                    <button onClick={() => save(m)}
                                            data-testid={`mfr-save-${m.id}`}
                                            className="bg-brand hover:bg-brand-hover text-black px-4 py-2 font-display uppercase tracking-wider text-xs inline-flex items-center gap-2">
                                        <Save size={12} /> Save changes
                                    </button>
                                )}
                                <PurchaseOrderEmail manufacturerId={m.id} manufacturerName={m.name} />
                                {m.contact_phone && (
                                    <WhatsAppPO manufacturerId={m.id} manufacturerName={m.name} />
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
